import ApiClient from "@/libs/http-client";

const state = {};
const mutations = {};

const actions = {
  // ------------------------------ Payouts ------------------------------
  getPayouts({ commit }, params = {}) {
    return ApiClient.get('api/payouts', { params });
  },
  getPayoutById({ commit }, { id } = {}) {
    if (!id) throw new Error('getPayoutById: "id" is required');
    return ApiClient.get(`api/payouts/${id}`);
  },
  createPayout({ commit }, payload = {}) {
    const data = normalizePayoutPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.post('api/payouts', data);
  },
  updatePayout({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updatePayout: "id" is required');
    const data = normalizePayoutPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/payouts/${id}`, data);
  },
  deletePayout({ commit }, { id } = {}) {
    if (!id) throw new Error('deletePayout: "id" is required');
    return ApiClient.delete(`api/payouts/${id}`);
  },

  // ------------------------- General legal entities -----------------------
    getGeneralLegalEntities({ commit }) {
        return new Promise((resolve, reject) => {
            ApiClient.get('/api/legal-entities', {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'multipart/form-data',
                },
            })
                .then((response) => resolve(response))
                .catch((error) => reject(error))
        })
    },

  // ------------------------- User legal entities -----------------------
  getUserLegalEntities({ commit }, { user_id, active } = {}) {
    const params = {};
    if (user_id != null) params.user_id = user_id;
    if (typeof active !== 'undefined') params.active = active ? 1 : 0;
    return ApiClient.get('api/user-legal-entities', { params });
  },
  getUserLegalEntityById({ commit }, { id } = {}) {
    if (!id) throw new Error('getUserLegalEntityById: "id" is required');
    return ApiClient.get(`api/user-legal-entities/${id}`);
  },
  createUserLegalEntity({ commit }, payload = {}) {
    const data = { ...payload };
    if (typeof data.active === 'boolean') data.active = data.active ? 1 : 0;
    if ('id' in data) delete data.id;
    return ApiClient.post('api/user-legal-entities', data);
  },
  updateUserLegalEntity({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updateUserLegalEntity: "id" is required');
    const data = { ...payload };
    if (typeof data.active === 'boolean') data.active = data.active ? 1 : 0;
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/user-legal-entities/${id}`, data);
  },
  deleteUserLegalEntity({ commit }, { id } = {}) {
    if (!id) throw new Error('deleteUserLegalEntity: "id" is required');
    return ApiClient.delete(`api/user-legal-entities/${id}`);
  },

  // --------------------- User legal entity accounts --------------------
  getUserLegalEntityAccounts({ commit }, { legal_entity_id, active } = {}) {
    const params = {};
    if (typeof legal_entity_id !== 'undefined' && legal_entity_id !== null) {
      params.legal_entity_id = legal_entity_id;
    }
    if (typeof active !== 'undefined') params.active = active ? 1 : 0;
    return ApiClient.get('api/user-legal-entity-accounts', { params });
  },
  getUserLegalEntityAccountById({ commit }, { id } = {}) {
    if (!id) throw new Error('getUserLegalEntityAccountById: "id" is required');
    return ApiClient.get(`api/user-legal-entity-accounts/${id}`);
  },
  createUserLegalEntityAccount({ commit }, payload = {}) {
    const data = { ...payload };
    if (typeof data.active === 'boolean') data.active = data.active ? 1 : 0;
    if (typeof data.is_default === 'boolean') data.is_default = data.is_default ? 1 : 0;
    if ('id' in data) delete data.id;
    return ApiClient.post('api/user-legal-entity-accounts', data);
  },
  updateUserLegalEntityAccount({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updateUserLegalEntityAccount: "id" is required');
    const data = { ...payload };
    if (typeof data.active === 'boolean') data.active = data.active ? 1 : 0;
    if (typeof data.is_default === 'boolean') data.is_default = data.is_default ? 1 : 0;
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/user-legal-entity-accounts/${id}`, data);
  },
  deleteUserLegalEntityAccount({ commit }, { id } = {}) {
    if (!id) throw new Error('deleteUserLegalEntityAccount: "id" is required');
    return ApiClient.delete(`api/user-legal-entity-accounts/${id}`);
  },

  // --------------------------- Payout payments -------------------------
  getPayoutPayments() {
    return ApiClient.get('api/payout-payments');
  },
  getPayoutPaymentById({ commit }, { id } = {}) {
    if (!id) throw new Error('getPayoutPaymentById: "id" is required');
    return ApiClient.get(`api/payout-payments/${id}`);
  },
  createPayoutPayment({ commit }, payload = {}) {
    const data = { ...payload };
    if ('id' in data) delete data.id;
    return ApiClient.post('api/payout-payments', data);
  },
  updatePayoutPayment({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updatePayoutPayment: "id" is required');
    const data = { ...payload };
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/payout-payments/${id}`, data);
  },
  deletePayoutPayment({ commit }, { id } = {}) {
    if (!id) throw new Error('deletePayoutPayment: "id" is required');
    return ApiClient.delete(`api/payout-payments/${id}`);
  },

  // ---------------------------- Expense Types --------------------------
  getExpenseTypes({ commit }) {
    return ApiClient.get('api/expense-types');
  },
  getExpenseTypeById({ commit }, { id }) {
    if (!id) throw new Error('getExpenseTypeById: "id" is required');
    return ApiClient.get(`api/expense-types/${id}`);
  },
  createExpenseType({ commit }, payload = {}) {
    const data = {
      name: String(payload.name || '').trim(),
      ...(typeof payload.is_taxable !== 'undefined' ? { is_taxable: !!payload.is_taxable } : {}),
      ...(typeof payload.is_rolling !== 'undefined' ? { is_rolling: !!payload.is_rolling } : {}),
    };
    return ApiClient.post('api/expense-types', data);
  },
  updateExpenseType({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updateExpenseType: "id" is required');
    const data = {
      ...(payload.name != null ? { name: String(payload.name).trim() } : {}),
      ...(typeof payload.is_taxable !== 'undefined' ? { is_taxable: !!payload.is_taxable } : {}),
      ...(typeof payload.is_rolling !== 'undefined' ? { is_rolling: !!payload.is_rolling } : {}),
    };
    return ApiClient.put(`api/expense-types/${id}`, data);
  },
  deleteExpenseType({ commit }, { id }) {
    if (!id) throw new Error('deleteExpenseType: "id" is required');
    return ApiClient.delete(`api/expense-types/${id}`);
  },

  // ------------------------ Expense Type Entities ----------------------
  getExpenseTypeEntities({ commit }, { expense_type_id, active } = {}) {
    const params = {};
    if (typeof expense_type_id !== 'undefined' && expense_type_id !== null) {
      params.expense_type_id = expense_type_id;
    }
    if (typeof active !== 'undefined') params.active = active ? 1 : 0;
    return ApiClient.get('api/expense-type-entities', { params });
  },
  getExpenseTypeEntityById({ commit }, { id } = {}) {
    if (!id) throw new Error('getExpenseTypeEntityById: "id" is required');
    return ApiClient.get(`api/expense-type-entities/${id}`);
  },
  createExpenseTypeEntity({ commit }, payload = {}) {
    const data = { ...payload };
    if ('id' in data) delete data.id;
    if (typeof data.name !== 'undefined') data.name = String(data.name || '').trim();
    ['is_taxable', 'is_rolling', 'is_default', 'is_monthly', 'active'].forEach((k) => {
      if (typeof data[k] !== 'undefined') data[k] = !!data[k];
    });
    ['amount', 'rate', 'base', 'percent', 'quantity'].forEach((k) => {
      if (data[k] !== undefined && data[k] !== null && data[k] !== '') {
        const n = Number(data[k]);
        if (!Number.isNaN(n)) data[k] = n;
      }
    });
    return ApiClient.post('api/expense-type-entities', data);
  },
  updateExpenseTypeEntity({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updateExpenseTypeEntity: "id" is required');
    const data = { ...payload };
    if ('id' in data) delete data.id;
    if (typeof data.name !== 'undefined') data.name = String(data.name || '').trim();
    ['is_taxable', 'is_rolling', 'is_default', 'is_monthly', 'active'].forEach((k) => {
      if (typeof data[k] !== 'undefined') data[k] = !!data[k];
    });
    ['amount', 'rate', 'base', 'percent', 'quantity'].forEach((k) => {
      if (data[k] !== undefined && data[k] !== null && data[k] !== '') {
        const n = Number(data[k]);
        if (!Number.isNaN(n)) data[k] = n;
      }
    });
    return ApiClient.put(`api/expense-type-entities/${id}`, data);
  },
  deleteExpenseTypeEntity({ commit }, { id } = {}) {
    if (!id) throw new Error('deleteExpenseTypeEntity: "id" is required');
    return ApiClient.delete(`api/expense-type-entities/${id}`);
  },

  // ---------------------------- Payout statuses ------------------------
  getPayoutStatuses() {
    return ApiClient.get('api/payout-statuses');
  },
  getPayoutStatusById({ commit }, { id } = {}) {
    if (!id) throw new Error('getPayoutStatusById: "id" is required');
    return ApiClient.get(`api/payout-statuses/${id}`);
  },
  createPayoutStatus({ commit }, payload = {}) {
    const data = { name: String(payload.name || '').trim() };
    if (typeof payload.color !== 'undefined' && payload.color !== null && String(payload.color).trim() !== '') {
      data.color = String(payload.color).trim();
    }
    return ApiClient.post('api/payout-statuses', data);
  },
  updatePayoutStatus({ commit }, { id, ...payload } = {}) {
    if (!id) throw new Error('updatePayoutStatus: "id" is required');
    const data = {};
    if (typeof payload.name !== 'undefined') data.name = String(payload.name || '').trim();
    if (typeof payload.color !== 'undefined') data.color = String(payload.color || '').trim();
    return ApiClient.put(`api/payout-statuses/${id}`, data);
  },
  deletePayoutStatus({ commit }, { id } = {}) {
    if (!id) throw new Error('deletePayoutStatus: "id" is required');
    return ApiClient.delete(`api/payout-statuses/${id}`);
  },

  // ----------------------- User Legal Entity Types ---------------------
  getUserLegalEntityTypes(_, params = {}) {
    return ApiClient.get('api/user-legal-entity-types', { params });
  },
  getUserLegalEntityType(_, id) {
    if (!id) throw new Error('getUserLegalEntityType: "id" is required');
    return ApiClient.get(`api/user-legal-entity-types/${id}`);
  },
  createUserLegalEntityType(_, payload) {
    return ApiClient.post('api/user-legal-entity-types', payload);
  },
  updateUserLegalEntityType(_, { id, data }) {
    if (!id) throw new Error('updateUserLegalEntityType: "id" is required');
    return ApiClient.put(`api/user-legal-entity-types/${id}`, data);
  },
  deleteUserLegalEntityType(_, id) {
    if (!id) throw new Error('deleteUserLegalEntityType: "id" is required');
    return ApiClient.delete(`api/user-legal-entity-types/${id}`);
  },

  // ---------------------------- Income Statuses --------------------------
  getIncomeStatuses(_, params = {}) {
    return ApiClient.get('api/income-statuses', { params });
  },
  getIncomeStatusById(_, { id } = {}) {
    if (!id) throw new Error('getIncomeStatusById: "id" is required');
    return ApiClient.get(`api/income-statuses/${id}`);
  },
  createIncomeStatus(_, payload = {}) {
    const data = {
      name: String(payload.name || '').trim(),
    };
    if (!data.name) throw new Error('createIncomeStatus: "name" is required');

    if (typeof payload.color !== 'undefined' && payload.color !== null && String(payload.color).trim() !== '') {
      data.color = String(payload.color).trim();
    }
    return ApiClient.post('api/income-statuses', data);
  },
  updateIncomeStatus(_, { id, ...payload } = {}) {
    if (!id) throw new Error('updateIncomeStatus: "id" is required');
    const data = {};
    if (typeof payload.name !== 'undefined') data.name = String(payload.name || '').trim();
    if (typeof payload.color !== 'undefined') data.color = String(payload.color || '').trim();

    return ApiClient.put(`api/income-statuses/${id}`, data);
  },
  deleteIncomeStatus(_, { id } = {}) {
    if (!id) throw new Error('deleteIncomeStatus: "id" is required');
    return ApiClient.delete(`api/income-statuses/${id}`);
  },


  // =========================== FIN PLAN: Periods ===========================
  getFinPlanPeriods(_, params = {}) {
    return ApiClient.get('api/fin-plan/periods', { params });
  },
  getFinPlanPeriodById(_, { id }) {
    if (!id) throw new Error('getFinPlanPeriodById: "id" is required');
    return ApiClient.get(`api/fin-plan/periods/${id}`);
  },
  createFinPlanPeriod(_, payload = {}) {
    const data = normalizeFinPeriodPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.post('api/fin-plan/periods', data);
  },
  updateFinPlanPeriod(_, { id, ...payload } = {}) {
    if (!id) throw new Error('updateFinPlanPeriod: "id" is required');
    const data = normalizeFinPeriodPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/fin-plan/periods/${id}`, data);
  },
  deleteFinPlanPeriod(_, { id }) {
    if (!id) throw new Error('deleteFinPlanPeriod: "id" is required');
    return ApiClient.delete(`api/fin-plan/periods/${id}`);
  },

  // =========================== FIN PLAN: Records ===========================
  getFinPlanRecords(_, params = {}) {
    return ApiClient.get('api/fin-plan/records', { params });
  },
  getFinPlanRecordById(_, { id }) {
    if (!id) throw new Error('getFinPlanRecordById: "id" is required');
    return ApiClient.get(`api/fin-plan/records/${id}`);
  },
  createFinPlanRecord(_, payload = {}) {
    const data = normalizeFinRecordPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.post('api/fin-plan/records', data);
  },
  updateFinPlanRecord(_, { id, ...payload } = {}) {
    if (!id) throw new Error('updateFinPlanRecord: "id" is required');
    const data = normalizeFinRecordPayload(payload);
    if ('id' in data) delete data.id;
    return ApiClient.put(`api/fin-plan/records/${id}`, data);
  },
  deleteFinPlanRecord(_, { id }) {
    if (!id) throw new Error('deleteFinPlanRecord: "id" is required');
    return ApiClient.delete(`api/fin-plan/records/${id}`);
  },


  // ================================ SETTINGS ===============================
  // list
  getSettings(_, params = {}) {
    return ApiClient.get('api/settings', { params });
  },
  // get by id
  getSettingById(_, { id }) {
    if (!id) throw new Error('getSettingById: "id" is required');
    return ApiClient.get(`api/settings/${id}`);
  },
  // update by id
  updateSetting(_, { id, name, value, block_id }) {
    if (!id) throw new Error('updateSetting: "id" is required');
    const body = {};

    body.name = name != null ? String(name) : null;
    body.value = value === null ? null : String(value ?? '');
    if (block_id != null) body.block_id = Number(block_id);

    return ApiClient.put(`api/settings/${id}`, body);
  },


  getSettingByKey(_, { key }) {
    if (!key) throw new Error('getSettingByKey: "key" is required');
    return ApiClient.get(`api/settings/by-key/${encodeURIComponent(key)}`);
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

/* --------------------------------- helpers -------------------------------- */

// Normalizes payout payload (your original logic kept intact)
function normalizePayoutPayload(src = {}) {
  const out = { ...src };
  Object.keys(out).forEach((k) => {
    if (out[k] === undefined || out[k] === null || out[k] === '') delete out[k];
  });
  ['user_id', 'legal_entity_id', 'bank_account_id', 'type_id', 'currency_id', 'parts'].forEach((k) => {
    if (k in out) out[k] = toInt(out[k]);
  });
  ['amount', 'tax_amount'].forEach((k) => {
    if (k in out) out[k] = toNumber(out[k]);
  });
  return out;
}

// Fin Plan Periods: { name, start_date, end_date, currency_rates? (json) }
function normalizeFinPeriodPayload(src = {}) {
  const out = { ...src };

  // Trim empty values
  Object.keys(out).forEach((k) => {
    if (out[k] === undefined || out[k] === null || out[k] === '') delete out[k];
  });

  if (typeof out.name !== 'undefined') out.name = String(out.name || '').trim();

  // Dates: keep as-is (ISO strings). If Date object passed — cast to ISO.
  ['start_date', 'end_date'].forEach((k) => {
    if (out[k] instanceof Date) out[k] = out[k].toISOString();
  });

  // currency_rates can be object or string; pass-through but ensure JSON on object
  if (out.currency_rates && typeof out.currency_rates === 'object') {
    try { out.currency_rates = JSON.stringify(out.currency_rates); } catch (_) { }
  }

  return out;
}

function normalizeFinRecordPayload(src = {}) {
  const out = { ...src };

  const invoiceIdToPreserve = out.invoice_id === null ? null : undefined;
  if (invoiceIdToPreserve === null) delete out.invoice_id;

  Object.keys(out).forEach((k) => {
    if (out[k] === undefined || out[k] === null || out[k] === '') delete out[k];
  });

  if (invoiceIdToPreserve === null) {
    out.invoice_id = null;
  }

  ['period_id', 'project_id', 'legal_entity_id', 'currency_id'].forEach((k) => {
    if (k in out) out[k] = toInt(out[k]);
  });

  if (typeof out.status_id !== 'undefined') {
    const raw = out.status_id;
    const n = Number(raw);
    if (Number.isFinite(n)) {
      out.status_id = parseInt(n, 10);
    } else {
      const s = String(raw || '').trim();
      if (s) out.status_id = s; else delete out.status_id;
    }
  }

  if (typeof out.services !== 'undefined') {
    const s = String(out.services || '').trim();
    if (s) out.services = s; else delete out.services;
  }

  ['amount', 'tax_amount'].forEach((k) => {
    if (k in out) out[k] = toNumber(out[k]);
  });

  if (typeof out.exchange_rate !== 'undefined') {
    const norm = normalizeAsJsonString(out.exchange_rate);
    if (norm != null) out.exchange_rate = norm;
    else delete out.exchange_rate;
  }

  if (typeof out.notes !== 'undefined') {
    out.notes = String(out.notes || '').trim();
    if (out.notes === '') delete out.notes;
  }

  return out;
}



function normalizeAsJsonString(value) {
  try {
    if (value == null || value === '') return null;

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return null;
      if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
        const parsed = JSON.parse(s);
        return JSON.stringify(parsed);
      }

      return null;
    }
    return null;
  } catch {
    return null;
  }
}

function toInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? parseInt(n, 10) : undefined;
}
function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
